import { apiRequest } from './client'
import type { BannerSeverity } from '../components/banners/bannerSeverity'

export type BannerDisplayMode = 'banner' | 'popup'
export type BannerDismissMode = 'one_time' | 'recurrent'
export type BannerAudienceMode = 'all' | 'authenticated'

/** Display-only projection returned by GET /banners/active. */
export interface ActiveBanner {
  id: string
  /** English base copy — also the universal fallback. */
  title: string
  message: string
  /**
   * Non-English locale overrides, `{locale: text}`. Empty for untranslated banners.
   * Resolved client-side by `resolveBannerText` so a language switch is an instant
   * re-render off the cached payload rather than a refetch.
   */
  title_i18n: Record<string, string>
  message_i18n: Record<string, string>
  display_mode: BannerDisplayMode
  dismiss_mode: BannerDismissMode
  severity: BannerSeverity
  /** When the banner went live (start_at ISO) — shown as "Posted: … SGT". */
  posted_at: string
  /** Opaque revision (server `updated_at` ISO) — keys one-time dismissal. */
  revision: string
}

interface ActiveBannersResponse {
  banners: ActiveBanner[]
}

/** Full admin view (GET/POST/PATCH /admin/banners). */
export interface AdminBanner {
  id: string
  title: string
  message: string
  title_i18n: Record<string, string>
  message_i18n: Record<string, string>
  display_mode: BannerDisplayMode
  dismiss_mode: BannerDismissMode
  severity: BannerSeverity
  audience_mode: BannerAudienceMode
  start_at: string
  end_at: string | null
  is_active: boolean
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface BannerCreateInput {
  title: string
  message: string
  /**
   * Non-English overrides only. An `en` key is accepted by the API solely when it
   * matches the base field (and is then dropped); a differing value is a 422.
   * English has one source of truth: the base column.
   */
  title_i18n?: Record<string, string>
  message_i18n?: Record<string, string>
  display_mode: BannerDisplayMode
  dismiss_mode: BannerDismissMode
  severity: BannerSeverity
  audience_mode: BannerAudienceMode
  start_at?: string | null
  end_at?: string | null
  is_active: boolean
}

export type BannerUpdateInput = Partial<BannerCreateInput>

const JSON_HEADERS = { 'Content-Type': 'application/json' }

// ---- Public read path ------------------------------------------------------

export async function fetchActiveBanners(): Promise<ActiveBanner[]> {
  const res = await apiRequest<ActiveBannersResponse>('/banners/active')
  return res.banners
}

/** Persist a one-time dismissal (authenticated users only). */
export async function dismissBanner(bannerId: string): Promise<void> {
  await apiRequest<void>(`/banners/${bannerId}/dismiss`, { method: 'POST' })
}

// ---- Admin CRUD ------------------------------------------------------------

export async function adminListBanners(): Promise<AdminBanner[]> {
  return apiRequest<AdminBanner[]>('/admin/banners')
}

export async function adminCreateBanner(input: BannerCreateInput): Promise<AdminBanner> {
  return apiRequest<AdminBanner>('/admin/banners', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  })
}

export async function adminUpdateBanner(
  id: string,
  input: BannerUpdateInput,
): Promise<AdminBanner> {
  return apiRequest<AdminBanner>(`/admin/banners/${id}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  })
}

export async function adminDeleteBanner(id: string): Promise<void> {
  await apiRequest<void>(`/admin/banners/${id}`, { method: 'DELETE' })
}
